import type {
  GamePhase,
  GameState,
  NightRoleStep,
  PlayerState,
  RoleId,
} from "../types";

export type ProjectedCardVisibility = "hidden" | "known" | "revealed";

export interface ProjectedCard {
  readonly cardId: string;
  readonly roleId: RoleId | null;
  readonly visibility: ProjectedCardVisibility;
}

export interface ProjectedNightRoleStep {
  readonly roleId: RoleId;
  readonly status: NightRoleStep["status"];
}

export type ProjectedRevealedCardLocation =
  | {
      readonly kind: "player";
      readonly playerId: string;
      readonly cardId: string;
    }
  | {
      readonly kind: "center";
      readonly slotIndex: 0 | 1 | 2;
      readonly cardId: string;
    };

export type ProjectedViewerNightActionStatus =
  | "required"
  | "waiting"
  | "completed";

export interface ProjectedViewerNightAction {
  readonly roleId: RoleId;
  readonly status: ProjectedViewerNightActionStatus;
  readonly canInspectCenter?: boolean;
  /** Actor-only: the role the doppelganger copied (never sent to others). */
  readonly copiedRoleId?: RoleId;
}

export interface ProjectedViewerState {
  readonly viewerPlayerId: string;
  readonly gameId: string;
  readonly createdAt: string;
  readonly version: number;
  readonly phase: GamePhase;
  readonly hostPlayerId: string;
  readonly players: readonly PlayerState[];
  readonly seatOrder: readonly string[];
  readonly seatLocked: boolean;
  readonly selectedRoleIds: readonly RoleId[];
  readonly rolePeekConfirmedPlayerIds: readonly string[];
  readonly discussionDurationSeconds: number;
  readonly discussionRemainingSeconds: number | null;
  readonly voteWindowSeconds: number;
  readonly voteRemainingSeconds: number | null;
  readonly autoVotePlayerIds: readonly string[];
  readonly voteLockedPlayerIds: readonly string[];
  readonly centerSlots: GameState["centerSlots"];
  readonly nightRoleQueue: readonly ProjectedNightRoleStep[];
  readonly announcerTimeline: GameState["announcerTimeline"];
  readonly viewerNightAction: ProjectedViewerNightAction | null;
  readonly voteResult: GameState["voteResult"];
  readonly revealedCardIds: readonly string[];
  readonly revealedCardLocations: readonly ProjectedRevealedCardLocation[];
  readonly gameOutcome: GameState["gameOutcome"];
  readonly viewerCurrentCardId: string | null;
  readonly visibleCards: Readonly<Record<string, ProjectedCard>>;
}

function visibleCard(
  state: GameState,
  viewerPlayerId: string,
  cardId: string,
): ProjectedCard {
  const card = state.cards.find((candidate) => candidate.id === cardId);
  const viewer = state.players.find((player) => player.id === viewerPlayerId);
  const isOwnStartingCard = viewer?.startingCardId === cardId;
  const isPrivateKnowledge =
    state.privateKnowledge[viewerPlayerId]?.includes(cardId) ?? false;
  const isRevealed = state.revealedCardIds.includes(cardId);

  if (isRevealed) {
    return { cardId, roleId: card?.roleId ?? null, visibility: "revealed" };
  }

  if (isOwnStartingCard || isPrivateKnowledge) {
    return { cardId, roleId: card?.roleId ?? null, visibility: "known" };
  }

  return { cardId, roleId: null, visibility: "hidden" };
}

function publicPlayers(state: GameState): readonly PlayerState[] {
  return state.players.map((player) => ({
    ...player,
    voteTargetId: state.voteResult ? player.voteTargetId : null,
  }));
}

function voteLockedPlayerIds(state: GameState): readonly string[] {
  return state.players.flatMap((player) =>
    player.voteTargetId ? [player.id] : [],
  );
}

function revealedCardLocations(
  state: GameState,
): readonly ProjectedRevealedCardLocation[] {
  const locations: ProjectedRevealedCardLocation[] = [];
  for (const cardId of state.revealedCardIds) {
    const position = state.cardPositions[cardId];
    if (!position) continue;
    if (position.zone === "player") {
      locations.push({ kind: "player", playerId: position.playerId, cardId });
      continue;
    }
    locations.push({ kind: "center", slotIndex: position.slotIndex, cardId });
  }
  return locations;
}

function currentCardIdAtPlayer(
  state: GameState,
  playerId: string,
): string | null {
  const entry = Object.entries(state.cardPositions).find(
    ([, position]) =>
      position.zone === "player" && position.playerId === playerId,
  );
  return entry?.[0] ?? null;
}

function currentPendingNightStep(state: GameState): NightRoleStep | null {
  return state.nightRoleQueue.find((step) => step.status === "pending") ?? null;
}

function viewerNightAction(
  state: GameState,
  viewerPlayerId: string,
): ProjectedViewerNightAction | null {
  const step = currentPendingNightStep(state);
  if (!step) return null;

  if (!step.actorPlayerIds.includes(viewerPlayerId)) {
    return { roleId: step.roleId, status: "waiting" };
  }
  const canInspectCenter =
    step.roleId === "werewolf" && step.actorPlayerIds.length === 1;
  const copiedRoleId =
    step.roleId === "doppelganger"
      ? (state.doppelgangerCopiedRoleId ?? null)
      : null;
  if (step.completedPlayerIds.includes(viewerPlayerId)) {
    return {
      roleId: step.roleId,
      status: "completed",
      ...(canInspectCenter ? { canInspectCenter: true } : {}),
      ...(copiedRoleId ? { copiedRoleId } : {}),
    };
  }
  return {
    roleId: step.roleId,
    status: "required",
    ...(canInspectCenter ? { canInspectCenter: true } : {}),
    ...(copiedRoleId ? { copiedRoleId } : {}),
  };
}

/**
 * Per-viewer redacted projection. A viewerPlayerId that is not a seated
 * player (e.g. an observer/replay viewer) sees only public + revealed state.
 */
export function projectGameStateForViewer(
  state: GameState,
  viewerPlayerId: string,
): ProjectedViewerState {
  return {
    viewerPlayerId,
    gameId: state.gameId,
    createdAt: state.createdAt,
    version: state.version,
    phase: state.phase,
    hostPlayerId: state.hostPlayerId,
    players: publicPlayers(state),
    seatOrder: state.seatOrder,
    seatLocked: state.seatLocked,
    selectedRoleIds: state.selectedRoleIds,
    rolePeekConfirmedPlayerIds: state.rolePeekConfirmedPlayerIds,
    discussionDurationSeconds: state.discussionDurationSeconds,
    discussionRemainingSeconds: state.discussionRemainingSeconds,
    voteWindowSeconds: state.voteWindowSeconds,
    voteRemainingSeconds: state.voteRemainingSeconds,
    autoVotePlayerIds: state.autoVotePlayerIds,
    voteLockedPlayerIds: voteLockedPlayerIds(state),
    centerSlots: state.centerSlots,
    nightRoleQueue: state.nightRoleQueue.map((step) => ({
      roleId: step.roleId,
      status: step.status,
    })),
    announcerTimeline: state.announcerTimeline,
    viewerNightAction: viewerNightAction(state, viewerPlayerId),
    voteResult: state.voteResult,
    revealedCardIds: state.revealedCardIds,
    revealedCardLocations: revealedCardLocations(state),
    gameOutcome: state.gameOutcome,
    viewerCurrentCardId: currentCardIdAtPlayer(state, viewerPlayerId),
    visibleCards: Object.fromEntries(
      state.cards.map((card) => [
        card.id,
        visibleCard(state, viewerPlayerId, card.id),
      ]),
    ),
  };
}
